<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <form class="finance-form" @submit.prevent="saveSettings()">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
                <div class="md:flex md:items-center md:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ pageHeading }}</h1>
                    </div>
                    <div class="mt-4 flex items-center md:mt-0 md:ml-4">
                        <router-link class="btn btn-secondary rounded mr-4" :to="financeListPath">
                            {{ $t('Close') }}
                        </router-link>
                        <button v-if="canUpdateFinanceEntry" class="btn btn-gray rounded" type="submit">
                            {{ $t('Save') }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="mt-6">
                    <loading :status="loading"/>

                    <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
                        <div class="order-2 lg:order-2 lg:col-span-5 xl:col-span-4 space-y-6">
                            <app-panel
                                :title="$t('Calculation')"
                                collapsible
                                :default-open="true"
                                body-class="px-8 py-6 space-y-1"
                            >
                                    <div class="text-sm text-gray-600">{{ $t('Net') }}: {{ formatCurrency(previewNetAmount) }}</div>
                                    <div v-if="showPreviewQuantification" class="text-sm text-gray-600">
                                        Inkl. QF ({{ previewQuantificationFactor }} x {{ previewQuantificationValueNormalized.toFixed(2) }}, {{ formatCurrency(previewQuantificationAmount) }})
                                    </div>
                                    <div class="text-sm text-gray-600">{{ $t('VAT') }} ({{ Number(previewVatRate || 0).toFixed(2) }}%): {{ formatCurrency(previewVatAmount) }}</div>
                                    <div class="my-2 border-t-2 border-gray-700"></div>
                                    <div class="text-sm font-semibold text-gray-700">{{ $t('Total') }}: {{ formatCurrency(previewGrossAmount) }}</div>
                            </app-panel>
                            <app-panel
                                v-if="!isCreateMode"
                                :title="$t('Settlement status')"
                                collapsible
                                :default-open="true"
                                body-class="!p-0"
                                footer-class="finance-settlement-status-footer"
                            >
                                    <div
                                        v-for="(status, index) in settlementTimelineItems"
                                        :key="'settlement-status-' + status.value"
                                        class="finance-settlement-status-item"
                                    >
                                        <div class="finance-settlement-status-box">
                                            <div class="finance-settlement-status-axis" aria-hidden="true">
                                                <span
                                                    v-if="index > 0"
                                                    class="finance-settlement-status-line finance-settlement-status-line--before"
                                                ></span>
                                                <span
                                                    v-if="index < (settlementTimelineItems.length - 1)"
                                                    class="finance-settlement-status-line finance-settlement-status-line--after"
                                                ></span>
                                                <span
                                                    class="finance-settlement-status-dot"
                                                    :style="settlementTimelineDotStyle(status.color)"
                                                ></span>
                                            </div>
                                            <div class="finance-settlement-status-content">
                                                <div class="finance-settlement-status-title">{{ status.label }}</div>

                                                <div
                                                    v-if="status.value === 'open' && item.created_by_user"
                                                    class="stage-form-control-inactive mt-3 flex min-w-0 items-center rounded border border-bordergray-100 px-3 py-2"
                                                >
                                                    <img
                                                        :src="item.created_by_user.avatar || item.created_by_user.gravatar || '/images/default/icon.png'"
                                                        :alt="item.created_by_user.name"
                                                        class="h-6 w-6 flex-shrink-0 rounded-full object-cover"
                                                    >
                                                    <span class="ml-2 min-w-0 truncate text-sm font-normal text-gray-800">
                                                        Erstellt von {{ item.created_by_user.name }}
                                                    </span>
                                                </div>

                                            <template v-if="status.value === 'billed'">
                                                <template v-if="canEditSettlementStatus('billed')">
                                                    <div class="relative mt-3">
                                                        <input
                                                            ref="billedDateInput"
                                                            v-model="item.billed_at_input"
                                                            :class="['form-input block w-full finance-date-input', {'finance-date-input--empty': !item.billed_at_input}]"
                                                            type="date"
                                                        >
                                                        <button
                                                            class="finance-date-picker-btn"
                                                            type="button"
                                                            @click="openDatePicker('billed')"
                                                        >
                                                            <svg aria-hidden="true" class="h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                                <path d="M8 2v3M16 2v3M3.75 9.25h16.5M6.5 5.25h11A1.75 1.75 0 0119.25 7v12A1.75 1.75 0 0117.5 20.75h-11A1.75 1.75 0 014.75 19V7A1.75 1.75 0 016.5 5.25z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/>
                                                            </svg>
                                                        </button>
                                                    </div>
                                                    <div class="mt-3">
                                                        <label class="finance-field-label">{{ $t('Invoice number') }}</label>
                                                        <input
                                                            v-model="item.invoice_number"
                                                            class="form-input block w-full"
                                                            maxlength="120"
                                                            placeholder="z. B. RE-2026-0001"
                                                            type="text"
                                                        >
                                                    </div>
                                                </template>
                                                <template v-else>
                                                    <div v-if="getSettlementDisplayDate('billed')" class="mt-3">
                                                        <input
                                                            :value="formatDateDisplay(getSettlementDisplayDate('billed'))"
                                                            class="form-input block w-full"
                                                            disabled
                                                            type="text"
                                                        >
                                                    </div>
                                                    <div v-if="item.invoice_number" class="mt-3">
                                                        <label class="finance-field-label">{{ $t('Invoice number') }}</label>
                                                        <input
                                                            :value="item.invoice_number"
                                                            class="form-input block w-full"
                                                            disabled
                                                            type="text"
                                                        >
                                                    </div>
                                                </template>
                                            </template>

                                            <template v-if="status.value === 'paid'">
                                                <div v-if="canEditSettlementStatus('paid')" class="relative mt-3">
                                                    <input
                                                        ref="paidDateInput"
                                                        v-model="item.paid_at_input"
                                                        :class="['form-input block w-full finance-date-input', {'finance-date-input--empty': !item.paid_at_input}]"
                                                        type="date"
                                                    >
                                                    <button
                                                        class="finance-date-picker-btn"
                                                        type="button"
                                                        @click="openDatePicker('paid')"
                                                    >
                                                        <svg aria-hidden="true" class="h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                            <path d="M8 2v3M16 2v3M3.75 9.25h16.5M6.5 5.25h11A1.75 1.75 0 0119.25 7v12A1.75 1.75 0 0117.5 20.75h-11A1.75 1.75 0 014.75 19V7A1.75 1.75 0 016.5 5.25z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/>
                                                        </svg>
                                                    </button>
                                                </div>
                                                <div v-else-if="getSettlementDisplayDate('paid')" class="mt-3">
                                                    <input
                                                        :value="formatDateDisplay(getSettlementDisplayDate('paid'))"
                                                        class="form-input block w-full"
                                                        disabled
                                                        type="text"
                                                    >
                                                </div>
                                            </template>

                                            <template v-if="status.value === 'reversed'">
                                                <div v-if="getSettlementDisplayDate('reversed')" class="mt-3">
                                                    <input
                                                        :value="formatDateDisplay(getSettlementDisplayDate('reversed'))"
                                                        class="form-input block w-full"
                                                        disabled
                                                        type="text"
                                                    >
                                                </div>
                                                <div v-if="reversalInvoiceNumber" class="mt-3">
                                                    <label class="finance-field-label">Nummer des Korrekturbelegs</label>
                                                    <input
                                                        :value="reversalInvoiceNumber"
                                                        class="form-input block w-full"
                                                        disabled
                                                        type="text"
                                                    >
                                                </div>
                                            </template>

                                            <label
                                                v-if="canEditSettlementStatus(status.value)"
                                                class="finance-settlement-status-completed"
                                            >
                                                <input
                                                    v-model="selectedSettlementStatus"
                                                    class="form-radio h-4 w-4 text-gray-700"
                                                    type="radio"
                                                    :value="status.value"
                                                >
                                                <span class="ml-2 text-sm font-medium text-gray-800">{{ $t('Completed') }}</span>
                                            </label>
                                            </div>
                                        </div>
                                    </div>

                                    <template v-if="canDeleteManualEntry" #footer>
                                        <button
                                            class="btn-stage-danger px-5"
                                            type="button"
                                            @click="confirmDeleteManualEntry"
                                        >
                                            {{ $t('Delete') }}
                                        </button>
                                    </template>
                            </app-panel>
                            <app-panel
                                v-if="showReversalPanel"
                                title="Stornierung"
                                collapsible
                                :default-open="true"
                                body-class="px-6 py-6"
                                footer-class="finance-reversal-panel-footer"
                            >
                                <template v-if="hasReversal">
                                    <div class="space-y-4">
                                        <div>
                                            <label class="finance-field-label">Ursprüngliche Rechnungsnummer</label>
                                            <input
                                                :value="item.invoice_number || '–'"
                                                class="form-input block w-full"
                                                disabled
                                                type="text"
                                            >
                                        </div>
                                        <div v-if="reversalCorrectionDocumentNumber">
                                            <label class="finance-field-label">Nummer des Korrekturbelegs</label>
                                            <input
                                                :value="reversalCorrectionDocumentNumber"
                                                class="form-input block w-full"
                                                disabled
                                                type="text"
                                            >
                                        </div>
                                        <div>
                                            <label class="finance-field-label">Stornodatum</label>
                                            <input
                                                :value="reversalDisplayDate ? formatDateDisplay(reversalDisplayDate) : '–'"
                                                class="form-input block w-full"
                                                disabled
                                                type="text"
                                            >
                                        </div>
                                        <div v-if="reversalReason">
                                            <label class="finance-field-label">Grund</label>
                                            <textarea
                                                :value="reversalReason"
                                                class="form-input block w-full"
                                                disabled
                                                rows="3"
                                            ></textarea>
                                        </div>
                                    </div>
                                </template>
                                <p v-else class="text-sm leading-5 text-gray-500">
                                    Der bezahlte Finanzeintrag kann nicht mehr geändert werden. Eine Stornierung erzeugt einen verknüpften Korrektureintrag als vollständige Gegenbuchung über denselben Rechnungsbetrag.
                                </p>

                                <template v-if="canStartReversal" #footer>
                                    <button
                                        class="btn-stage-danger px-5"
                                        type="button"
                                        @click="openReversalDialog"
                                    >
                                        Stornieren
                                    </button>
                                </template>
                            </app-panel>
                            <finance-document-panel
                                v-if="!isCreateMode && routeEntryId"
                                :entry-id="routeEntryId"
                                @history-changed="refreshFinanceHistory"
                            />
                            <app-panel
                                v-if="showAuditPanel"
                                title="Historie"
                                collapsible
                                :default-open="true"
                                body-class="!p-0"
                            >
                                <div class="finance-history-list">
                                    <div
                                        v-for="event in financeHistory"
                                        :key="'finance-history-' + event.id"
                                        class="finance-history-entry"
                                    >
                                        <img
                                            v-if="getUserAvatar(event.actor)"
                                            :src="getUserAvatar(event.actor)"
                                            :alt="auditUserName(event.actor)"
                                            class="h-8 w-8 rounded-full object-cover flex-shrink-0"
                                        >
                                        <div :class="getUserAvatar(event.actor) ? 'ml-3' : ''" class="min-w-0">
                                            <div class="truncate text-sm font-medium leading-4 text-gray-800">{{ auditUserName(event.actor) }}</div>
                                            <time
                                                v-if="event.occurred_at"
                                                :datetime="event.occurred_at"
                                                class="mt-0.5 block text-xs leading-4 text-gray-500"
                                            >
                                                <span>{{ historyEventLabel(event) }} am {{ formatHistoryTimestamp(event) }}</span>
                                                <app-tooltip
                                                    v-if="historyDocumentName(event)"
                                                    :text="historyDocumentName(event)"
                                                    placement="bottom"
                                                    width-class="w-max max-w-xs break-words"
                                                    viewport
                                                    class="ml-1 flex-shrink-0"
                                                >
                                                    <svg-vue
                                                        class="h-3.5 w-3.5 text-gray-400"
                                                        icon="font-awesome.regular.circle-info"
                                                    ></svg-vue>
                                                </app-tooltip>
                                            </time>
                                            <div v-if="historyEventDetail(event)" class="truncate text-xs leading-5 text-gray-400">
                                                {{ historyEventDetail(event) }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </app-panel>
                        </div>

                        <div class="order-1 lg:order-1 lg:col-span-7 xl:col-span-8">
                            <app-panel :title="detailsPanelHeading" body-class="px-8 py-7 space-y-10">
                                    <section class="finance-details-fields-section">
                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div class="md:col-span-2" v-if="isPositionSource">
                                                <div class="flex items-start min-w-0">
                                                    <div v-if="positionProjectImages.length > 0" class="flex items-center flex-shrink-0 mt-0.5">
                                                        <img
                                                            v-for="(project, projectIndex) in positionProjectImages"
                                                            :key="'finance-position-project-' + project.id + '-' + projectIndex"
                                                            :src="project.image_url"
                                                            :title="project.title || 'Projekt'"
                                                            :alt="project.title || 'Projektbild'"
                                                            :class="projectIndex !== 0 ? '-ml-8' : ''"
                                                            class="h-10 w-10 rounded-sm object-cover border border-white shadow-solid"
                                                        >
                                                    </div>
                                                    <div :class="positionProjectImages.length > 0 ? 'ml-3 min-w-0 flex-1' : 'min-w-0 flex-1'">
                                                        <div class="stage-finance-group-title-row">
                                                            <div class="min-w-0 text-lg leading-5 font-semibold text-gray-900 truncate" :title="item.position_title || '-'">{{ item.position_title || '-' }}</div>
                                                            <identifier-badge v-if="item.position_id" :value="item.position_id" title="Positions-ID"></identifier-badge>
                                                        </div>
                                                        <router-link
                                                            v-if="item.position_id"
                                                            :to="'/workspace/positions/' + item.position_id + '/manage'"
                                                            :title="$t('Show position')"
                                                            :aria-label="$t('Show position')"
                                                            class="finance-position-link inline-flex text-sm leading-4 font-normal no-underline focus:outline-none"
                                                            rel="noopener noreferrer"
                                                            target="_blank"
                                                        >
                                                            Position öffnen
                                                        </router-link>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="md:col-span-2" v-else>
                                                <label class="finance-field-label">{{ $t('Title') }}</label>
                                                <input v-model="item.title" :disabled="isPaidLocked" class="form-input block w-full" required type="text">
                                            </div>
                                            <div v-if="isPositionSource && hasVisibleExportNote" class="md:col-span-2">
                                                <app-panel
                                                    :title="$t('Note (visible in export)')"
                                                    collapsible
                                                    :default-open="true"
                                                    variant="compact"
                                                    panel-class="shadow-none"
                                                    body-class="!px-5 !py-4"
                                                    title-tag="h4"
                                                >
                                                    <div class="text-sm text-gray-800 whitespace-pre-line">{{ visibleExportNote }}</div>
                                                </app-panel>
                                            </div>
                                            <div v-else-if="!isPositionSource" class="md:col-span-2">
                                                <label class="finance-field-label">{{ $t('Note (visible in export)') }}</label>
                                                <textarea v-model="item.description" :disabled="isPaidLocked" class="form-input block w-full" rows="3"></textarea>
                                            </div>
                                            <div v-if="isAdminActor" class="md:col-span-2">
                                                <label class="finance-field-label">{{ $t('Internal note') }}</label>
                                                <textarea v-model="item.internal_note" class="form-input block w-full" rows="4"></textarea>
                                            </div>
                                        </div>
                                    </section>

                                    <section>
                                        <div v-if="showAdminEntryScopeSelection" class="mb-6">
                                            <label class="finance-field-label">Art der Abrechnung</label>
                                            <select v-model="item.entry_scope" :disabled="isPaidLocked" class="form-select block w-full">
                                                <option value="income">{{ $t('Income') }}</option>
                                                <option value="expense">{{ $t('Expense') }}</option>
                                            </select>
                                        </div>
                                        <div v-else-if="showReadOnlyEntryScopeDisplay && !isPositionSource" class="mb-6">
                                            <label class="finance-field-label">Art der Abrechnung</label>
                                            <input
                                                :value="entryScopeLabel"
                                                class="form-input block w-full"
                                                disabled
                                                type="text"
                                            >
                                        </div>

                                        <div class="grid grid-cols-1 items-stretch gap-3 md:grid-cols-2 md:gap-4">
                                            <finance-service-party-card
                                                label="Leistungserbringer"
                                                :party="relationshipProviderParty"
                                                :editable="relationshipAssignmentEditable && item.entry_scope === 'expense'"
                                                :options="assignmentSelectionOptions"
                                                :selected-id="selectedAssignmentCompanyId"
                                                select-id="finance-service-provider-account"
                                                @select="setRecipientCompany"
                                            />

                                            <finance-service-party-card
                                                label="Leistungsempfänger"
                                                :party="relationshipRecipientParty"
                                                :editable="relationshipAssignmentEditable && item.entry_scope === 'income'"
                                                :options="assignmentSelectionOptions"
                                                :selected-id="selectedAssignmentCompanyId"
                                                select-id="finance-service-recipient-account"
                                                @select="setRecipientCompany"
                                            />
                                        </div>
                                    </section>

                                    <section>
                                        <h3 v-if="!showReadOnlyBillingSummary" class="text-base font-semibold text-gray-900 mb-4">{{ billingSectionHeading }}</h3>
                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div class="md:col-span-2">
                                                <template v-if="showReadOnlyBillingSummary">
                                                    <app-panel
                                                        :title="billingSectionHeading"
                                                        body-class="!p-0"
                                                        panel-class="shadow-none"
                                                        title-tag="h4"
                                                    >
                                                        <template #header-actions>
                                                            <button
                                                                class="finance-billing-summary-copy text-gray-700 hover:text-gray-900 focus:text-gray-900"
                                                                type="button"
                                                                title="Tabellendaten kopieren"
                                                                aria-label="Tabellendaten kopieren"
                                                                @click="copyBillingSummary"
                                                            >
                                                                <svg-vue class="h-4 w-4" icon="font-awesome.regular.clipboard"></svg-vue>
                                                            </button>
                                                        </template>
                                                        <dl class="divide-y divide-bordergray-100">
                                                            <div
                                                                v-for="(summaryItem, summaryIndex) in readOnlyBillingSummaryItems"
                                                                :key="'readonly-billing-summary-' + summaryIndex"
                                                                class="px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4"
                                                            >
                                                                <dt class="text-sm text-gray-500">{{ summaryItem.label }}</dt>
                                                                <dd :class="summaryItem.valueClass || 'text-sm text-gray-900 sm:text-right'">{{ summaryItem.value }}</dd>
                                                            </div>
                                                        </dl>
                                                    </app-panel>
                                                    <div v-if="isPositionSource" class="mt-3 px-1 text-xs text-gray-500">
                                                        Aus der Kostenfreigabe der Position übernommen.
                                                    </div>
                                                </template>
                                                <template v-else>
                                                    <div class="payment-type-fields-frame">
                                                        <div>
                                                            <label class="finance-field-label">{{ $t('Payment type') }}</label>
                                                            <select v-model="item.amount_type" class="form-select block w-full">
                                                                <option value="hourly">{{ $t('Hourly') }}</option>
                                                                <option value="fixed">{{ $t('Fixed') }}</option>
                                                            </select>
                                                        </div>
                                                        <div class="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                                                            <div v-if="item.amount_type === 'hourly'">
                                                                <label class="finance-field-label">{{ $t('Quantity (hours)') }}</label>
                                                                <input v-model.number="item.quantity" class="form-input block w-full" min="0" step="0.01" type="number">
                                                            </div>
                                                            <div v-if="item.amount_type === 'hourly'">
                                                                <label class="finance-field-label">{{ $t('Hourly rate (net)') }}</label>
                                                                <input v-model.number="item.hourly_rate" class="form-input block w-full" disabled min="0" step="0.01" type="number">
                                                            </div>
                                                            <div v-if="item.amount_type === 'fixed'" class="md:col-span-2">
                                                                <label class="finance-field-label">{{ $t('Fixed amount (net)') }}</label>
                                                                <input v-model.number="item.fixed_amount" class="form-input block w-full" step="0.01" type="number">
                                                            </div>
                                                            <div>
                                                                <label class="finance-field-label">{{ $t('VAT rate (%)') }}</label>
                                                                <input v-model.number="item.vat_rate" class="form-input block w-full" disabled min="0" step="0.01" type="number">
                                                            </div>
                                                        </div>
                                                    </div>
                                                </template>
                                            </div>
                                        </div>
                                    </section>

                                    <section v-if="!isPaidLocked && !isPositionSource">
                                        <h3 class="text-base font-semibold text-gray-900 mb-4">{{ $t('Quantification') }}</h3>
                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label class="finance-field-label">{{ $t('QF (integer)') }}</label>
                                                <input v-model.number="item.quantification_factor" class="form-input block w-full" min="0" step="1" type="number">
                                            </div>
                                            <div>
                                                <label class="finance-field-label">{{ $t('QW (value, default 0.25)') }}</label>
                                                <input v-model.number="item.quantification_value" class="form-input block w-full" min="0" step="0.01" type="number">
                                            </div>
                                        </div>
                                    </section>

                            </app-panel>
                        </div>
                    </div>
                </div>
            </div>
        </form>
        <confirm-dialog
            :open="confirmDialog.open"
            :title="confirmDialog.title"
            :text="confirmDialog.text"
            :confirm-label="confirmDialog.confirmLabel || $t('Save').toString()"
            :confirm-button-class="confirmDialog.confirmButtonClass || 'btn btn-gray'"
            headline-id="finance-confirm-modal-headline"
            @close="closeConfirmDialog"
            @confirm="confirmDialogAction"
        />
        <app-dialog
            :open="reversalDialog.open"
            title="Finanzeintrag stornieren"
            size="md"
            :busy="reversalSubmitting"
            @close="closeReversalDialog"
        >
            <form id="finance-reversal-form" class="finance-form space-y-6" @submit.prevent="submitReversal">
                <p class="text-sm leading-5 text-gray-500">
                    Der ursprüngliche Finanzeintrag bleibt unverändert erhalten. Die Stornierung wird durch einen eigenständigen, verknüpften Korrektureintrag ausgeglichen.
                </p>

                <div>
                    <label class="finance-field-label">Ursprüngliche Rechnungsnummer</label>
                    <input
                        :value="item.invoice_number || '–'"
                        class="form-input block w-full"
                        disabled
                        type="text"
                    >
                </div>

                <div>
                    <label class="finance-field-label" for="finance-reversal-document-number">Nummer des Korrekturbelegs (optional)</label>
                    <input
                        id="finance-reversal-document-number"
                        v-model.trim="reversalDialog.correctionDocumentNumber"
                        class="form-input block w-full"
                        maxlength="120"
                        placeholder="z. B. ST-2026-0001"
                        type="text"
                    >
                    <p class="mt-2 text-xs leading-5 text-gray-500">{{ reversalDocumentNumberHelp }}</p>
                </div>

                <div>
                    <label class="finance-field-label" for="finance-reversal-date">Stornodatum</label>
                    <input
                        id="finance-reversal-date"
                        v-model="reversalDialog.date"
                        class="form-input block w-full"
                        required
                        type="date"
                    >
                </div>

                <div>
                    <label class="finance-field-label" for="finance-reversal-reason">Grund (optional)</label>
                    <textarea
                        id="finance-reversal-reason"
                        v-model.trim="reversalDialog.reason"
                        class="form-input block w-full"
                        maxlength="5000"
                        rows="4"
                    ></textarea>
                </div>
            </form>

            <template #footer>
                <app-dialog-action
                    form="finance-reversal-form"
                    label="Stornieren"
                    loading-label="Wird storniert …"
                    native-type="submit"
                    variant="danger"
                    :loading="reversalSubmitting"
                />
            </template>
        </app-dialog>
    </main>
</template>

<script>
import ConfirmDialog from '@/components/modals/confirm-dialog.vue';
import AppDialog from '@/components/modals/app-dialog.vue';
import AppDialogAction from '@/components/modals/app-dialog-action.vue';
import AppTooltip from '@/components/elements/tooltip.vue';
import {
    createDefaultConfirmDialog,
    createDefaultFinanceEntry,
    formatCurrency,
    formatDateDisplay,
    formatSignedCurrency,
    normalizeSettlementStatuses as buildSettlementStatuses,
    settlementTimelineDotStyle,
    toDateInput,
} from '@/utils/workspace-finance-settings';
import {resolveReturnToPath} from "@/utils/list-route-state";
import FinanceDocumentPanel from '@/components/finance/finance-document-panel.vue';
import FinanceServicePartyCard from '@/components/finance/finance-service-party-card.vue';

function createDefaultReversalDialog() {
    return {
        open: false,
        correctionDocumentNumber: '',
        date: null,
        reason: '',
    };
}

export default {
    name: 'finance-settings',
    components: {AppDialog, AppDialogAction, AppTooltip, ConfirmDialog, FinanceDocumentPanel, FinanceServicePartyCard},
    metaInfo() {
        return {
            title: this.pageHeading
        }
    },
    data() {
        return {
            loading: false,
            users: [],
            companies: [],
            manual_income_company_options: [],
            manual_expense_company_options: [],
            sender_options: [],
            recipient_options: [],
            admin_users: [],
            operator_service_party: null,
            actor: null,
            item: createDefaultFinanceEntry(),
            confirmDialog: createDefaultConfirmDialog(),
            selectedSettlementStatus: 'open',
            reversalInvoiceNumber: '',
            reversalDateInput: null,
            reversalDialog: createDefaultReversalDialog(),
            reversalSubmitting: false,
        };
    },
    computed: {
        financeListPath() {
            return resolveReturnToPath(this.$route.query || {}, '/workspace/finance');
        },
        pageHeading() {
            if (!this.isCreateMode) {
                return this.isManualSource ? 'Manueller Finanzeintrag' : 'Finanzeinstellungen';
            }

            return 'Finanzeintrag anlegen';
        },
        detailsPanelHeading() {
            return 'Finanzdetails';
        },
        billingSectionHeading() {
            return (this.isPaidLocked || this.isPositionSource) ? 'Abrechnungsdetails' : this.$t('Payment');
        },
        settlementTimelineItems() {
            const items = this.normalizeSettlementStatuses(this.item.settlement_statuses || []);
            const currentStatus = String(this.item && this.item.settlement_status ? this.item.settlement_status : 'open').toLowerCase();
            const isPaidOrBeyond = ['paid', 'reversed'].includes(currentStatus) || !!(this.item && this.item.paid_at);

            if (this.isEmployeeActor) {
                return items.filter((status) => {
                    if (status.value === 'paid') {
                        return isPaidOrBeyond;
                    }

                    if (status.value === 'reversed') {
                        return currentStatus === 'reversed' || this.hasReversal;
                    }

                    return true;
                });
            }
            const shouldShowReversed = this.hasReversal || currentStatus === 'reversed';

            return items.filter((status) => status.value !== 'reversed' || shouldShowReversed);
        },
        isCreateMode() {
            return !this.routeEntryId;
        },
        routeEntryId() {
            return this.$route.params.entryId || null;
        },
        isPositionSource() {
            return [
                'position_effort_income',
                'position_effort_expense',
            ].includes(String(this.item.source_type || '').toLowerCase());
        },
        isPositionExpenseSource() {
            return String(this.item.source_type || '').toLowerCase() === 'position_effort_expense';
        },
        isPositionIncomeSource() {
            const sourceType = String(this.item.source_type || '').toLowerCase();
            return sourceType === 'position_effort_income';
        },
        positionProjectImages() {
            const projects = Array.isArray(this.item?.projects) && this.item.projects.length > 0
                ? this.item.projects
                : (this.item?.project ? [this.item.project] : []);

            return projects
                .filter(Boolean)
                .map((project, index) => ({
                    ...project,
                    id: project.id || `project-${index}`,
                    image_url: String(project.image_url || '').trim(),
                }))
                .filter((project) => project.image_url !== '');
        },
        positionProjectNames() {
            const projects = Array.isArray(this.item?.projects) && this.item.projects.length > 0
                ? this.item.projects
                : (this.item?.project ? [this.item.project] : []);
            const names = projects
                .map((project) => String(project?.title || '').trim())
                .filter((title, index, values) => title !== '' && values.indexOf(title) === index);

            return names.length > 0 ? names.join(', ') : '-';
        },
        visibleExportNote() {
            return String(this.item?.description || '').trim();
        },
        hasVisibleExportNote() {
            return this.visibleExportNote !== '';
        },
        currentUserId() {
            return this.$store && this.$store.state && this.$store.state.user
                ? Number(this.$store.state.user.id || 0)
                : 0;
        },
        isAdminActor() {
            if (this.actor && this.actor.is_admin !== undefined) {
                return !!this.actor.is_admin;
            }
            const user = this.$store && this.$store.state ? this.$store.state.user : null;
            return !!(user && user.access && user.access.is_admin);
        },
        isEmployeeActor() {
            if (this.actor && this.actor.is_employee_like !== undefined) {
                return !!this.actor.is_employee_like;
            }

            const user = this.$store && this.$store.state ? this.$store.state.user : null;
            return !!(user && user.access && user.access.is_employee_like);
        },
        assignmentSelectionOptions() {
            if (this.item.entry_scope === 'expense') {
                return this.manualExpenseCompanyOptions;
            }

            return this.manualIncomeCompanyOptions;
        },
        manualIncomeCompanyOptions() {
            return this.normalizeCompanyOptions(this.manual_income_company_options || this.companies || []);
        },
        manualExpenseCompanyOptions() {
            return this.normalizeCompanyOptions(this.manual_expense_company_options || this.companies || []);
        },
        selectedAssignmentCompanyId() {
            const serviceAccountId = Number(this.item.service_account_id || 0);
            if (serviceAccountId > 0) {
                return serviceAccountId;
            }
            if (this.item.entry_scope === 'expense') {
                return Number(this.item.manual_expense_company_id || this.item.provider_company_id || 0);
            }

            return Number(this.item.manual_income_company_id || this.item.customer_company_id || 0);
        },
        selectedAssignmentAccount() {
            const selectedId = Number(this.selectedAssignmentCompanyId || 0);
            if (selectedId < 1) return null;

            return (this.assignmentSelectionOptions || []).find(
                (option) => Number(option?.id || 0) === selectedId
            ) || null;
        },
        selectedAssignmentParty() {
            return this.normalizeServiceAccountOptionParty(this.selectedAssignmentAccount);
        },
        storedServiceRelationship() {
            return this.item && this.item.service_relationship
                ? this.item.service_relationship
                : {};
        },
        relationshipProviderParty() {
            if (this.isPositionSource || !this.relationshipAssignmentEditable) {
                return this.storedServiceRelationship.provider || null;
            }

            if (this.item.entry_scope === 'expense') {
                return this.selectedAssignmentParty
                    || this.storedServiceRelationship.provider
                    || null;
            }

            return this.operator_service_party
                || this.storedServiceRelationship.provider
                || null;
        },
        relationshipRecipientParty() {
            if (this.isPositionSource || !this.relationshipAssignmentEditable) {
                return this.storedServiceRelationship.recipient || null;
            }

            if (this.item.entry_scope === 'expense') {
                return this.operator_service_party
                    || this.storedServiceRelationship.recipient
                    || null;
            }

            return this.selectedAssignmentParty
                || this.storedServiceRelationship.recipient
                || null;
        },
        firstAdminRecipient() {
            if ((this.admin_users || []).length > 0) {
                return this.admin_users[0];
            }

            return (this.users || []).find((user) => !!user.is_admin) || null;
        },
        canEditAssignments() {
            return this.isAdminActor
                && this.canUpdateFinanceEntry
                && !(this.item && this.item.is_locked);
        },
        hasReversal() {
            return !!(this.item && (this.item.has_reversal || (this.item.reversal_entry && this.item.reversal_entry.id)));
        },
        reversalEntry() {
            return this.item && this.item.reversal_entry ? this.item.reversal_entry : null;
        },
        reversalCorrectionDocumentNumber() {
            if (!this.reversalEntry) return '';

            return String(
                this.reversalEntry.correction_document_number
                || this.reversalEntry.invoice_number
                || ''
            );
        },
        reversalDisplayDate() {
            if (!this.reversalEntry) return null;

            return this.reversalEntry.paid_at || this.reversalEntry.billed_at || this.reversalEntry.created_at || null;
        },
        reversalReason() {
            return this.reversalEntry ? String(this.reversalEntry.reason || '') : '';
        },
        isManualSource() {
            return String(this.item && this.item.source_type ? this.item.source_type : '').toLowerCase() === 'manual';
        },
        showReversalPanel() {
            return !this.isCreateMode
                && this.isManualSource
                && (this.isPaidLocked || this.hasReversal)
                && (this.isAdminActor || this.hasReversal);
        },
        canStartReversal() {
            return this.showReversalPanel
                && !this.hasReversal
                && !!(this.item && this.item.can_reverse);
        },
        reversalDocumentNumberHelp() {
            if (String(this.item && this.item.entry_scope ? this.item.entry_scope : '').toLowerCase() === 'expense') {
                return 'Falls vorhanden, tragen Sie die Nummer des erhaltenen Storno-, Gutschrift- oder Korrekturbelegs ein.';
            }

            return 'Falls vorhanden, tragen Sie die Nummer Ihrer Korrektur- oder Stornorechnung ein.';
        },
        isPaidLocked() {
            return !!this.item
                && !!this.item.is_locked
                && String(this.item.settlement_status || '').toLowerCase() === 'paid';
        },
        canDeleteManualEntry() {
            if (this.isCreateMode) {
                return false;
            }

            if (this.item && this.item.can_delete !== undefined && this.item.can_delete !== null) {
                return !!this.item.can_delete;
            }

            return !!(this.item && this.item.id)
                && String(this.item.source_type || '').toLowerCase() === 'manual'
                && !this.item.is_locked
                && !['paid', 'reversed'].includes(this.effectiveSettlementStatus);
        },
        canUpdateFinanceEntry() {
            if (this.isCreateMode) {
                return true;
            }

            if (this.item && this.item.can_update !== undefined && this.item.can_update !== null) {
                return !!this.item.can_update;
            }

            return !this.isPaidLocked;
        },
        showAuditPanel() {
            return !this.isCreateMode && this.financeHistory.length > 0;
        },
        financeHistory() {
            return Array.isArray(this.item && this.item.history) ? this.item.history : [];
        },
        effectiveSettlementStatus() {
            const normalized = String(this.item && this.item.settlement_status ? this.item.settlement_status : 'open').toLowerCase();
            if (['open', 'billed', 'paid', 'reversed'].includes(normalized)) {
                return normalized;
            }

            return 'open';
        },
        showReadOnlyRecipientDisplay() {
            return this.isPositionSource || ['paid', 'reversed'].includes(this.effectiveSettlementStatus);
        },
        showAssignmentSelection() {
            return !(this.isCreateMode && this.isEmployeeActor);
        },
        relationshipAssignmentEditable() {
            return this.isCreateMode
                && !this.isPositionSource
                && this.canEditAssignments
                && this.showAssignmentSelection;
        },
        showAdminEntryScopeSelection() {
            return this.isCreateMode
                && this.isAdminActor
                && !this.isPositionSource
                && !this.isPaidLocked;
        },
        showReadOnlyEntryScopeDisplay() {
            return !this.isPositionSource && !this.showAdminEntryScopeSelection;
        },
        showReadOnlyBillingSummary() {
            return this.isPaidLocked || this.isPositionSource;
        },
        isEmployeeCompensationLocked() {
            return this.isEmployeeActor && !this.isPositionSource;
        },
        employeeHourlyRate() {
            const actorRate = this.actor && this.actor.provider_hourly_rate !== null && this.actor.provider_hourly_rate !== undefined && this.actor.provider_hourly_rate !== ''
                ? Number(this.actor.provider_hourly_rate)
                : null;
            if (actorRate !== null && !Number.isNaN(actorRate)) {
                return actorRate;
            }

            return 80;
        },
        employeeVatRate() {
            const actorRate = this.actor && this.actor.provider_vat_rate !== null && this.actor.provider_vat_rate !== undefined && this.actor.provider_vat_rate !== ''
                ? Number(this.actor.provider_vat_rate)
                : null;
            if (actorRate !== null && !Number.isNaN(actorRate)) {
                return actorRate;
            }

            return 19;
        },
        previewBaseAmount() {
            if (this.isPositionSource) {
                return this.isPositionExpenseSource
                    ? Number(this.item.internal_base_amount || 0)
                    : Number(this.item.customer_base_amount || 0);
            }

            if (this.item.amount_type === 'fixed') {
                return Number(this.item.fixed_amount || 0);
            }

            return Number(this.item.quantity || 0) * Number(this.item.hourly_rate || 0);
        },
        previewVatRate() {
            return Number(this.item.vat_rate || 0);
        },
        previewQuantificationFactor() {
            return Math.max(0, Math.round(Number(this.item.quantification_factor || 0)));
        },
        previewQuantificationValueNormalized() {
            const rawQw = Number(this.item.quantification_value || 0);
            const normalized = rawQw > 1 && rawQw <= 100 ? (rawQw / 100) : rawQw;
            return Math.max(0, normalized);
        },
        previewQuantificationAmount() {
            const base = Number(this.previewBaseAmount || 0);
            return Math.round((base * this.previewQuantificationFactor * this.previewQuantificationValueNormalized) * 100) / 100;
        },
        showPreviewQuantification() {
            return this.previewQuantificationFactor > 0;
        },
        previewNetAmount() {
            const base = Number(this.previewBaseAmount || 0);
            return Math.round((base + this.previewQuantificationAmount) * 100) / 100;
        },
        previewVatAmount() {
            return Math.round((this.previewNetAmount * (this.previewVatRate / 100)) * 100) / 100;
        },
        previewGrossAmount() {
            return Math.round((this.previewNetAmount + this.previewVatAmount) * 100) / 100;
        },
        amountTypeLabel() {
            return this.item.amount_type === 'fixed'
                ? this.$t('Fixed').toString()
                : this.$t('Hourly').toString();
        },
        entryScopeLabel() {
            return this.item.entry_scope === 'expense'
                ? this.$t('Expense').toString()
                : this.$t('Income').toString();
        },
        paymentSummaryScopeHours() {
            if (this.isPositionSource) {
                return this.isPositionExpenseSource
                    ? Number(this.item.internal_hours || 0)
                    : Number(this.item.customer_hours || 0);
            }

            if (this.item.amount_type === 'fixed') {
                return 0;
            }

            return Number(this.item.quantity || 0);
        },
        paymentSummaryHourlyRateValue() {
            return this.formatCurrency(Number(this.item.hourly_rate || this.employeeHourlyRate || 0)) + ' / Std.';
        },
        paymentSummaryItems() {
            const items = [];

            if (this.item.amount_type === 'fixed') {
                items.push(
                    {
                        label: this.$t('Payment type').toString(),
                        value: this.amountTypeLabel,
                    },
                    {
                        label: this.$t('Fixed amount (net)').toString(),
                        value: this.formatCurrency(Number(this.item.fixed_amount || this.previewBaseAmount || 0)),
                    },
                    {
                        label: this.$t('VAT rate (%)').toString(),
                        value: `${Number(this.item.vat_rate || 0).toFixed(2)}%`,
                    },
                );

                return items;
            }

            items.push(
                {
                    label: this.$t('Payment type').toString(),
                    value: this.amountTypeLabel,
                },
                {
                    label: this.$t('Hourly rate (net)').toString(),
                    value: this.paymentSummaryHourlyRateValue,
                },
                {
                    label: this.$t('Scope').toString(),
                    value: `${this.paymentSummaryScopeHours.toFixed(2)} Std.`,
                },
                {
                    label: 'MwSt',
                    value: `${Number(this.item.vat_rate || 0).toFixed(2)}%`,
                },
            );

            return items;
        },
        readOnlyBillingSummaryItems() {
            const items = [];

            if (this.isPositionSource) {
                items.push(
                    {
                        label: 'Positions-ID',
                        value: String(this.item.position_id || '-'),
                    },
                    {
                        label: 'Position',
                        value: this.item.position_title || '-',
                    },
                    {
                        label: 'Projekte',
                        value: this.positionProjectNames,
                    },
                    {
                        label: 'Leistung',
                        value: this.item.service_category_name || '-',
                    },
                );
            }

            if (this.showReadOnlyEntryScopeDisplay) {
                items.push({
                    label: 'Art der Abrechnung',
                    value: this.entryScopeLabel,
                    valueClass: 'text-sm font-semibold text-gray-900 sm:text-right',
                });
            }

            items.push(
                ...this.paymentSummaryItems,
                {
                    label: 'Quantifizierungsfaktor (QF)',
                    value: String(this.previewQuantificationFactor),
                },
                {
                    label: 'Quantifizierungswert (QW)',
                    value: this.previewQuantificationValueNormalized.toFixed(2),
                },
            );

            return items;
        },
        billingSummaryClipboardText() {
            return this.readOnlyBillingSummaryItems
                .map((summaryItem) => `${summaryItem.label}: ${summaryItem.value}`)
                .join('\n');
        },
    },
    watch: {
        'item.entry_scope'() {
            if (this.showAdminEntryScopeSelection) {
                this.applyAssignmentDefaults();
            }
        },
    },
    mounted() {
        if (this.isCreateMode) {
            this.fetchOptions();
            return;
        }

        this.getSettings();
    },
    methods: {
        settlementTimelineDotStyle,
        async copyBillingSummary() {
            const text = this.billingSummaryClipboardText;
            if (text === '') {
                return;
            }

            try {
                let copied = false;
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    try {
                        await navigator.clipboard.writeText(text);
                        copied = true;
                    } catch (error) {
                        copied = false;
                    }
                }

                if (!copied) {
                    const textarea = document.createElement('textarea');
                    textarea.value = text;
                    textarea.setAttribute('readonly', 'readonly');
                    textarea.style.position = 'fixed';
                    textarea.style.opacity = '0';
                    document.body.appendChild(textarea);
                    try {
                        textarea.select();
                        copied = document.execCommand('copy');
                    } finally {
                        document.body.removeChild(textarea);
                    }
                }

                if (!copied) {
                    throw new Error('Copy command failed');
                }

                this.$notify({
                    title: this.$t('Success'),
                    text: 'Tabellendaten wurden kopiert.',
                    type: 'success',
                });
            } catch (error) {
                this.$notify({
                    title: this.$t('Error'),
                    text: 'Die Tabellendaten konnten nicht kopiert werden.',
                    type: 'error',
                });
            }
        },
        normalizeSettlementStatuses(statuses) {
            return buildSettlementStatuses(statuses, (key) => this.$t(key).toString());
        },
        fetchOptions() {
            const self = this;
            self.loading = true;
            axios.get('api/workspace/finance/options').then(function (response) {
                self.item.settlement_statuses = self.normalizeSettlementStatuses((response.data || {}).settlement_statuses || []);
                self.hydrateAssignmentOptions(response.data || {});
                self.applyAssignmentDefaults();
                self.applyEmployeeCompensationDefaults();
                self.syncSettlementStatusState();
                self.loading = false;
            }).catch(function () {
                self.loading = false;
            });
        },
        getSettings() {
            const self = this;
            self.loading = true;
            axios.get('api/workspace/finance/' + self.routeEntryId + '/settings').then(function (response) {
                self.item = Object.assign({}, self.item, response.data || {});
                self.item.settlement_statuses = self.normalizeSettlementStatuses(self.item.settlement_statuses || []);
                if (self.item.quantification_value === null || self.item.quantification_value === undefined) {
                    self.item.quantification_value = 0.25;
                }
                if (self.item.quantification_factor === null || self.item.quantification_factor === undefined) {
                    self.item.quantification_factor = 0;
                }
                if (self.item.invoice_number === undefined) {
                    self.item.invoice_number = null;
                }
                if (self.item.vat_rate === null || self.item.vat_rate === undefined || self.item.vat_rate === '') {
                    self.item.vat_rate = 19;
                }
                if (!self.item.entry_scope) {
                    self.item.entry_scope = Number(self.item.customer_service_account_id || 0) > 0
                        || Number(self.item.manual_income_company_id || self.item.customer_company_id || 0) > 0
                        || Number(self.item.customer_user_id || 0) > 0
                        ? 'income'
                        : 'expense';
                }
                self.item.billed_at_input = self.toDateInput(self.item.billed_at);
                self.item.paid_at_input = self.toDateInput(self.item.paid_at);
                self.hydrateAssignmentOptions(response.data || {});
                self.applyAssignmentDefaults();
                self.applyEmployeeCompensationDefaults();
                self.syncSettlementStatusState();
                self.loading = false;
            }).catch(function () {
                self.loading = false;
                self.$router.push('/workspace/finance');
            });
        },
        refreshFinanceHistory() {
            if (this.isCreateMode || !this.routeEntryId) {
                return;
            }

            axios.get('api/workspace/finance/' + this.routeEntryId + '/settings').then((response) => {
                this.$set(this.item, 'history', Array.isArray(response.data && response.data.history)
                    ? response.data.history
                    : []);
            });
        },
        syncSettlementStatusState() {
            this.selectedSettlementStatus = this.effectiveSettlementStatus;

            const reversalEntry = this.item && this.item.reversal_entry ? this.item.reversal_entry : null;
            this.reversalInvoiceNumber = reversalEntry && reversalEntry.invoice_number
                ? String(reversalEntry.invoice_number)
                : '';
            this.reversalDateInput = this.toDateInput(reversalEntry ? (reversalEntry.paid_at || reversalEntry.billed_at) : null);
        },
        hydrateAssignmentOptions(payload) {
            this.users = payload.users || this.users || [];
            this.companies = this.normalizeCompanyOptions(payload.companies || this.companies || []);
            this.manual_income_company_options = this.normalizeCompanyOptions(
                payload.manual_income_company_options || payload.customer_company_options || []
            );
            this.manual_expense_company_options = this.normalizeCompanyOptions(
                payload.manual_expense_company_options || payload.provider_company_options || []
            );
            this.sender_options = payload.sender_options || [];
            this.recipient_options = payload.recipient_options || [];
            this.admin_users = payload.admin_users || [];
            this.operator_service_party = payload.operator_service_party || this.operator_service_party;
            this.actor = payload.actor || this.actor;
        },
        canEditSettlementStatus(statusValue) {
            const normalizedValue = String(statusValue || '').toLowerCase();
            if (this.isEmployeeActor) {
                return ['open', 'billed'].includes(normalizedValue) && !this.isPaidLocked && !this.hasReversal;
            }

            if (normalizedValue === 'reversed') {
                return false;
            }

            return !this.isPaidLocked && !this.hasReversal;
        },
        applyAssignmentDefaults() {
            if (this.isPositionSource || !this.isCreateMode) {
                return;
            }

            if (this.isEmployeeActor) {
                this.item.entry_scope = 'expense';
            }

            const actorId = this.currentUserId > 0
                ? Number(this.currentUserId)
                : (this.actor ? Number(this.actor.id || 0) : 0);
            this.item.from_user_id = actorId > 0 ? actorId : null;

            if (this.isAdminActor) {
                const availableCompanies = this.assignmentSelectionOptions || [];
                const currentCompanyId = this.selectedAssignmentCompanyId;
                const hasCurrentCompany = availableCompanies.some((company) => Number(company.id) === currentCompanyId);
                const fallbackCompany = hasCurrentCompany
                    ? availableCompanies.find((company) => Number(company.id) === currentCompanyId)
                    : availableCompanies[0];
                const fallbackCompanyId = fallbackCompany ? Number(fallbackCompany.id) : null;
                this.item.service_account_id = fallbackCompanyId;

                if (this.item.entry_scope === 'expense') {
                    this.item.provider_service_account_id = fallbackCompanyId;
                    this.item.customer_service_account_id = null;
                    this.item.manual_expense_company_id = fallbackCompany && fallbackCompany.holder_company_id ? Number(fallbackCompany.holder_company_id) : null;
                    this.item.manual_expense_company_name_snapshot = fallbackCompany ? (fallbackCompany.holder_name || fallbackCompany.name) : null;
                    this.item.provider_company_id = this.item.manual_expense_company_id;
                    this.item.provider_company_name_snapshot = this.item.manual_expense_company_name_snapshot;
                    this.item.manual_income_company_id = null;
                    this.item.manual_income_company_name_snapshot = null;
                    this.item.customer_company_id = null;
                    this.item.customer_company_name_snapshot = null;
                } else {
                    this.item.customer_service_account_id = fallbackCompanyId;
                    this.item.provider_service_account_id = null;
                    this.item.manual_income_company_id = fallbackCompany && fallbackCompany.holder_company_id ? Number(fallbackCompany.holder_company_id) : null;
                    this.item.manual_income_company_name_snapshot = fallbackCompany ? (fallbackCompany.holder_name || fallbackCompany.name) : null;
                    this.item.customer_company_id = this.item.manual_income_company_id;
                    this.item.customer_company_name_snapshot = this.item.manual_income_company_name_snapshot;
                    this.item.manual_expense_company_id = null;
                    this.item.manual_expense_company_name_snapshot = null;
                    this.item.provider_company_id = null;
                    this.item.provider_company_name_snapshot = null;
                }

                this.item.to_user_id = null;
                this.applySelectedCompanyCompensationDefaults();
                return;
            }

            if (this.isEmployeeActor) {
                this.item.to_user_id = this.firstAdminRecipient ? Number(this.firstAdminRecipient.id) : null;
                this.item.manual_income_company_id = null;
                this.item.manual_income_company_name_snapshot = null;
                this.item.customer_company_id = null;
                this.item.customer_company_name_snapshot = null;
                this.item.manual_expense_company_id = this.actor && Number(this.actor.company_id || 0) > 0
                    ? Number(this.actor.company_id)
                    : (Number(this.item.manual_expense_company_id || 0) || null);
                this.item.manual_expense_company_name_snapshot = this.actor && this.actor.company_name
                    ? this.actor.company_name
                    : this.item.manual_expense_company_name_snapshot;
                this.item.provider_company_id = this.actor && Number(this.actor.company_id || 0) > 0
                    ? Number(this.actor.company_id)
                    : (Number(this.item.provider_company_id || 0) || null);
                this.item.provider_company_name_snapshot = this.actor && this.actor.company_name
                    ? this.actor.company_name
                    : this.item.provider_company_name_snapshot;
                const providerAccount = (this.manualExpenseCompanyOptions || [])[0] || null;
                this.item.service_account_id = providerAccount ? Number(providerAccount.id) : null;
                this.item.provider_service_account_id = this.item.service_account_id;
            }
        },
        applyEmployeeCompensationDefaults() {
            if (!this.isCreateMode || !this.isEmployeeCompensationLocked) {
                return;
            }

            this.item.hourly_rate = this.employeeHourlyRate;
            this.item.vat_rate = this.employeeVatRate;
        },
        resolveSelectedCompanyCompensationDefaults() {
            const company = this.resolveCompanyById(this.selectedAssignmentCompanyId);
            if (!company) {
                return {
                    hourly_rate: null,
                    vat_rate: null,
                };
            }

            if (this.item.entry_scope === 'expense') {
                return {
                    hourly_rate: company.provider_hourly_rate !== null && company.provider_hourly_rate !== undefined && company.provider_hourly_rate !== ''
                        ? Number(company.provider_hourly_rate)
                        : null,
                    vat_rate: company.provider_vat_rate !== null && company.provider_vat_rate !== undefined && company.provider_vat_rate !== ''
                        ? Number(company.provider_vat_rate)
                        : null,
                };
            }

            return {
                hourly_rate: company.customer_hourly_rate !== null && company.customer_hourly_rate !== undefined && company.customer_hourly_rate !== ''
                    ? Number(company.customer_hourly_rate)
                    : null,
                vat_rate: company.customer_vat_rate !== null && company.customer_vat_rate !== undefined && company.customer_vat_rate !== ''
                    ? Number(company.customer_vat_rate)
                    : null,
            };
        },
        applySelectedCompanyCompensationDefaults() {
            if (this.isPositionSource || this.isEmployeeActor) {
                return;
            }

            const rates = this.resolveSelectedCompanyCompensationDefaults();
            this.item.hourly_rate = rates.hourly_rate !== null && !Number.isNaN(rates.hourly_rate)
                ? rates.hourly_rate
                : 0;
            this.item.vat_rate = rates.vat_rate !== null && !Number.isNaN(rates.vat_rate)
                ? rates.vat_rate
                : 0;
        },
        setRecipientCompany(companyId) {
            if (!this.relationshipAssignmentEditable) {
                return;
            }
            const company = this.resolveCompanyById(companyId);
            const normalizedCompanyId = Number(companyId || 0) || null;
            const holderCompanyId = company && Number(company.holder_company_id || 0) > 0
                ? Number(company.holder_company_id)
                : null;
            this.item.service_account_id = normalizedCompanyId;

            if (this.item.entry_scope === 'expense') {
                this.item.manual_expense_company_id = holderCompanyId;
                this.item.manual_expense_company_name_snapshot = company ? (company.holder_name || company.name) : null;
                this.item.provider_service_account_id = normalizedCompanyId;
                this.item.provider_company_id = holderCompanyId;
                this.item.provider_company_name_snapshot = this.item.manual_expense_company_name_snapshot;
                this.item.manual_income_company_id = null;
                this.item.manual_income_company_name_snapshot = null;
                this.item.customer_company_id = null;
                this.item.customer_company_name_snapshot = null;
                this.applySelectedCompanyCompensationDefaults();
                return;
            }

            this.item.manual_income_company_id = holderCompanyId;
            this.item.manual_income_company_name_snapshot = company ? (company.holder_name || company.name) : null;
            this.item.customer_service_account_id = normalizedCompanyId;
            this.item.customer_company_id = holderCompanyId;
            this.item.customer_company_name_snapshot = this.item.manual_income_company_name_snapshot;
            this.item.manual_expense_company_id = null;
            this.item.manual_expense_company_name_snapshot = null;
            this.item.provider_company_id = null;
            this.item.provider_company_name_snapshot = null;
            this.applySelectedCompanyCompensationDefaults();
        },
        getUserAvatar(user) {
            if (!user) {
                return '';
            }
            if (user.image) {
                return user.image;
            }
            if (user.avatar && user.avatar !== 'gravatar') {
                return user.avatar;
            }
            return user.gravatar || user.avatar || '';
        },
        normalizeServiceAccountOptionParty(option) {
            if (!option) {
                return null;
            }

            const accountId = Number(option.service_account_id || option.id || 0);
            const accountName = String(option.name || '').trim();
            const displayName = String(option.holder_name || option.name || '').trim();

            if (accountId < 1 && !accountName && !displayName) {
                return null;
            }

            return {
                account_id: accountId > 0 ? accountId : null,
                account_name: accountName || null,
                display_name: displayName || null,
                avatar: this.getUserAvatar(option) || null,
                address: String(option.address || '').trim() || null,
            };
        },
        auditUserName(user) {
            return user && user.name ? user.name : '-';
        },
        formatAuditDateTime(value) {
            return this.$momentFormatDateTime(value).replace(/\s+(\d{1,2}:\d{2})$/, ', $1');
        },
        formatHistoryTimestamp(event) {
            const metadata = event && event.metadata ? event.metadata : {};
            const effectiveDate = String(metadata.effective_date || '').slice(0, 10);
            if (metadata.legacy && /^\d{4}-\d{2}-\d{2}$/.test(effectiveDate)) {
                const parts = effectiveDate.split('-');
                return `${parts[2]}.${parts[1]}.${parts[0]}`;
            }

            return this.formatAuditDateTime(event && event.occurred_at ? event.occurred_at : '');
        },
        historyEventLabel(event) {
            const labels = {
                entry_created: 'Erstellt',
                entry_updated: 'Bearbeitet',
                status_billed: 'Abgerechnet',
                status_paid: 'Als bezahlt markiert',
                status_reopened: 'Auf Offen zurückgesetzt',
                entry_reversed: 'Storniert',
                correction_entry_created: 'Korrektureintrag erstellt',
                document_linked: 'Dokument verknüpft',
                document_unlinked: 'Dokument entfernt',
                document_replaced: 'Dokument ersetzt',
                document_deleted: 'Dokument gelöscht',
            };

            return labels[String(event && event.event_type ? event.event_type : '')] || 'Aktualisiert';
        },
        historyDocumentName(event) {
            const eventType = String(event && event.event_type ? event.event_type : '');
            if (!eventType.startsWith('document_')) {
                return '';
            }

            const metadata = event && event.metadata ? event.metadata : {};
            return String(metadata.document_name || '').trim();
        },
        historyEventDetail(event) {
            const metadata = event && event.metadata ? event.metadata : {};
            const eventType = String(event && event.event_type ? event.event_type : '');
            if (eventType === 'correction_entry_created' || eventType === 'entry_reversed') {
                const number = String(metadata.correction_document_number || '').trim();
                return number ? `Korrekturbeleg ${number}` : '';
            }

            return '';
        },
        normalizeCompanyOptions(collection) {
            const items = Array.isArray(collection) ? collection : [];

            return items
                .map((company) => {
                    if (!company || Number(company.id || 0) < 1) {
                        return null;
                    }

                    return {
                        ...company,
                        id: Number(company.id),
                    };
                })
                .filter(Boolean);
        },
        resolveUserName(userId) {
            const user = this.resolveUserById(userId);
            return user ? user.name : '';
        },
        resolveUserById(userId) {
            const normalizedId = Number(userId || 0);
            if (normalizedId < 1) {
                return null;
            }

            const sources = [
                this.users || [],
                this.recipient_options || [],
                this.sender_options || [],
                this.admin_users || [],
            ];

            for (const source of sources) {
                const match = (source || []).find((item) => Number(item.id) === normalizedId);
                if (match) {
                    return match;
                }
            }

            return null;
        },
        resolveCompanyById(companyId) {
            const normalizedId = Number(companyId || 0);
            if (normalizedId < 1) {
                return null;
            }

            const sources = [
                this.companies || [],
                this.manual_income_company_options || [],
                this.manual_expense_company_options || [],
            ];

            for (const source of sources) {
                const match = (source || []).find((item) => Number(item.id) === normalizedId);
                if (match) {
                    return match;
                }
            }

            return null;
        },
        openConfirmDialog({
            title = '',
            text = '',
            confirmLabel = '',
            confirmButtonClass = 'btn btn-gray mr-2 sm:mr-0',
            pendingAction = null,
        } = {}) {
            this.confirmDialog = {
                open: true,
                title,
                text,
                confirmLabel,
                confirmButtonClass,
                pendingAction,
            };
        },
        closeConfirmDialog() {
            this.confirmDialog = createDefaultConfirmDialog();
        },
        confirmDialogAction() {
            const action = this.confirmDialog.pendingAction;
            this.closeConfirmDialog();

            if (action === 'save-paid') {
                this.saveSettings(true);
                return;
            }

            if (action === 'delete-manual-entry') {
                this.deleteManualEntry();
            }
        },
        confirmDeleteManualEntry() {
            this.openConfirmDialog({
                title: this.$t('Delete entry?').toString(),
                text: this.$t('Manual finance entries can only be deleted before they are marked as paid. Delete this entry now?').toString(),
                confirmLabel: this.$t('Delete').toString(),
                confirmButtonClass: 'btn btn-red mr-2 sm:mr-0',
                pendingAction: 'delete-manual-entry',
            });
        },
        deleteManualEntry() {
            const self = this;
            if (!self.routeEntryId || !self.canDeleteManualEntry) {
                return;
            }

            self.loading = true;
            axios.delete('api/workspace/finance/' + self.routeEntryId).then(function () {
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$t('Finance entry was deleted').toString(),
                    type: 'success'
                });
                self.$router.push('/workspace/finance');
            }).catch(function () {
                self.loading = false;
            });
        },
        saveSettings(force = false) {
            const self = this;

            if (!self.canUpdateFinanceEntry) {
                return;
            }

            self.applyAssignmentDefaults();
            self.applyEmployeeCompensationDefaults();

            const activeSettlementStatus = self.isCreateMode
                ? 'open'
                : String(self.selectedSettlementStatus || self.item.settlement_status || 'open').toLowerCase();
            const invoiceNumber = String(self.item.invoice_number || '').trim();
            if (['billed', 'paid'].includes(activeSettlementStatus) && invoiceNumber === '') {
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: self.$t('The invoice number is required for settlement status Billed/Paid/Reversed.').toString(),
                    type: 'error'
                });
                self.loading = false;
                return;
            }
            self.item.invoice_number = invoiceNumber !== '' ? invoiceNumber : null;

            if (activeSettlementStatus === 'paid' && !self.item.is_locked && !force) {
                self.openConfirmDialog({
                    title: self.$t('Mark as paid?').toString(),
                    text: self.$t('Paid income entries will be locked after saving and can no longer be edited. Changes are only possible via reversal. Save as \"Paid\" now?').toString(),
                    confirmLabel: self.$t('Save').toString(),
                    confirmButtonClass: 'btn btn-gray mr-2 sm:mr-0',
                    pendingAction: 'save-paid',
                });
                return;
            }

            self.loading = true;

            const payload = {
                title: self.item.title,
                description: self.item.description,
                amount_type: self.item.amount_type,
                quantity: self.item.quantity,
                hourly_rate: self.item.hourly_rate,
                fixed_amount: self.item.fixed_amount,
                entry_scope: self.isAdminActor
                    ? (self.item.entry_scope === 'expense' ? 'expense' : 'income')
                    : 'expense',
                service_account_id: self.item.service_account_id,
                customer_company_id: self.item.entry_scope === 'expense' ? null : self.item.customer_company_id,
                provider_company_id: self.item.provider_company_id,
                from_user_id: self.item.from_user_id,
                to_user_id: self.item.to_user_id,
                quantification_factor: Math.max(0, Math.round(Number(self.item.quantification_factor || 0))),
                quantification_value: self.item.quantification_value === null || self.item.quantification_value === undefined
                    ? 0.25
                    : self.item.quantification_value,
                vat_rate: self.item.vat_rate,
                settlement_status: activeSettlementStatus,
                invoice_number: activeSettlementStatus === 'open' ? null : self.item.invoice_number,
                billed_at: activeSettlementStatus === 'open' ? null : self.item.billed_at_input,
                paid_at: activeSettlementStatus === 'paid' ? self.item.paid_at_input : null,
            };
            if (self.isAdminActor) {
                payload.internal_note = self.item.internal_note;
            }

            if (self.isCreateMode) {
                axios.post('api/workspace/finance', payload).then(function (response) {
                    const item = response.data || {};
                    self.loading = false;
                    self.$notify({
                        title: self.$i18n.t('Success').toString(),
                        text: self.$t('Income entry was created').toString(),
                        type: 'success'
                    });
                    if (item.id) {
                        self.$router.push('/workspace/finance/' + item.id + '/settings');
                        return;
                    }
                    self.$router.push('/workspace/finance');
                }).catch(function () {
                    self.loading = false;
                });
                return;
            }

            axios.put('api/workspace/finance/' + self.routeEntryId + '/settings', payload).then(function (response) {
                self.item = Object.assign({}, self.item, response.data || {});
                self.item.billed_at_input = self.toDateInput(self.item.billed_at);
                self.item.paid_at_input = self.toDateInput(self.item.paid_at);
                self.hydrateAssignmentOptions(response.data || {});
                self.applyAssignmentDefaults();
                self.applyEmployeeCompensationDefaults();
                self.syncSettlementStatusState();
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data updated correctly').toString(),
                    type: 'success'
                });
            }).catch(function () {
                self.loading = false;
            });
        },
        openReversalDialog() {
            if (!this.canStartReversal) return;

            this.reversalDialog = {
                open: true,
                correctionDocumentNumber: '',
                date: this.toDateInput(new Date()),
                reason: '',
            };
        },
        closeReversalDialog() {
            if (this.reversalSubmitting) return;

            this.reversalDialog = createDefaultReversalDialog();
        },
        submitReversal() {
            const correctionDocumentNumber = String(this.reversalDialog.correctionDocumentNumber || '').trim();
            const reversalDate = String(this.reversalDialog.date || '').trim();
            const reversalReason = String(this.reversalDialog.reason || '').trim();

            if (!this.canStartReversal || reversalDate === '') {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: 'Das Stornodatum ist erforderlich.',
                    type: 'error',
                });
                return;
            }

            this.reversalSubmitting = true;
            axios.post('api/workspace/finance/' + this.item.id + '/reverse', {
                correction_document_number: correctionDocumentNumber || null,
                reversal_at: reversalDate,
                reversal_reason: reversalReason || null,
            }).then((response) => {
                this.item = Object.assign({}, this.item, response.data || {});
                this.item.billed_at_input = this.toDateInput(this.item.billed_at);
                this.item.paid_at_input = this.toDateInput(this.item.paid_at);
                this.hydrateAssignmentOptions(response.data || {});
                this.applyAssignmentDefaults();
                this.applyEmployeeCompensationDefaults();
                this.syncSettlementStatusState();
                this.reversalSubmitting = false;
                this.reversalDialog = createDefaultReversalDialog();
                this.$notify({
                    title: this.$i18n.t('Success').toString(),
                    text: 'Der Finanzeintrag wurde storniert.',
                    type: 'success',
                });
            }).catch(() => {
                this.reversalSubmitting = false;
            });
        },
        toDateInput,
        formatDateDisplay,
        isCompanyCustomer(item) {
            return Number(item?.customer_company_id || 0) > 0;
        },
        getSettlementDisplayDate(type) {
            const billedDate = this.item.billed_at_input || this.toDateInput(this.item.billed_at);
            const paidDate = this.item.paid_at_input || this.toDateInput(this.item.paid_at);

            if (String(type) === 'billed') {
                return billedDate || paidDate || null;
            }

            if (String(type) === 'paid') {
                return paidDate || billedDate || null;
            }

            if (String(type) === 'reversed') {
                const reversalEntry = this.item && this.item.reversal_entry ? this.item.reversal_entry : null;
                return this.reversalDateInput
                    || this.toDateInput(reversalEntry ? (reversalEntry.paid_at || reversalEntry.billed_at) : null)
                    || null;
            }

            return null;
        },
        openDatePicker(field) {
            const refName = field === 'paid'
                ? 'paidDateInput'
                : (field === 'reversed' ? 'reversedDateInput' : 'billedDateInput');
            const inputRef = this.$refs[refName];
            const input = Array.isArray(inputRef) ? inputRef[0] : inputRef;

            if (!input || input.disabled) {
                return;
            }

            if (typeof input.showPicker === 'function') {
                input.showPicker();
                return;
            }

            input.focus();
            input.click();
        },
        formatCurrency,
        formatSignedCurrency,
    }
}
</script>

<style lang="scss">
@reference "../../../../../sass/tailwind-reference.css";

.finance-form {
    .finance-details-fields-section {
        margin-block-end: 1.5rem !important;
    }

    .finance-history-list {
        max-height: calc((3.75rem * 5) - 1px);
        overflow-x: hidden;
        overflow-y: auto;
    }

    .finance-history-entry {
        display: flex;
        min-width: 0;
        min-height: 3.75rem;
        align-items: center;
        padding: 0.75rem 1.5rem;
        border-bottom: 1px solid var(--ui-border-default);
    }

    .finance-history-entry:last-child {
        border-bottom: 0;
    }

    .finance-settlement-status-footer,
    .finance-reversal-panel-footer {
        display: flex;
        justify-content: flex-end;
    }

    .finance-settlement-status-item {
        border-bottom: 1px solid var(--ui-border-default);
    }

    .finance-settlement-status-item:last-child {
        border-bottom: 0;
    }

    .finance-settlement-status-item + .finance-settlement-status-item .finance-settlement-status-box::before {
        content: '';
        position: absolute;
        top: -1px;
        left: calc(1.5rem - 0.25rem);
        z-index: 1;
        width: 0.5625rem;
        height: 1px;
        background-color: var(--ui-surface-default);
    }

    .finance-settlement-status-box {
        position: relative;
        min-width: 0;
        padding: 1rem 1.5rem;
        background-color: var(--ui-surface-default);
    }

    .finance-settlement-status-axis {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 1rem;
        width: 1rem;
        pointer-events: none;
    }

    .finance-settlement-status-line {
        position: absolute;
        left: 50%;
        z-index: 2;
        width: 1px;
        background-color: var(--ui-border-default);
    }

    .finance-settlement-status-line--before {
        top: -1px;
        height: calc(1.5rem + 1px);
    }

    .finance-settlement-status-line--after {
        top: 1.5rem;
        bottom: -1px;
    }

    .finance-settlement-status-dot {
        position: absolute;
        top: 1.0625rem;
        left: 50%;
        z-index: 3;
        width: 0.875rem;
        height: 0.875rem;
        border-radius: 9999px;
        transform: translateX(-50%);
    }

    .finance-settlement-status-content {
        min-width: 0;
        padding-left: 2rem;
    }

    .finance-settlement-status-title {
        min-height: 1rem;
        font-size: 0.875rem;
        font-weight: 500;
        line-height: 1rem;
        color: var(--ui-text-primary);
    }

    .finance-settlement-status-completed {
        display: inline-flex;
        align-items: center;
        margin-top: 1rem;
    }

    .finance-field-label {
        display: block;
        font-size: 0.75rem;
        letter-spacing: 0.02em;
        color: var(--ui-text-muted);
        margin-bottom: 0.5rem;
        font-weight: 500;
        line-height: 1.25rem;
    }

    .form-input,
    .form-select,
    .form-textarea {
        width: 100%;
        border: 1px solid var(--ui-border-default);
        border-radius: 4px;
        box-shadow: none;
        background: #fff;
        color: var(--ui-text-heading);
        min-height: 2.5rem;
    }

    .form-input::placeholder,
    .form-textarea::placeholder {
        color: var(--ui-text-subtle);
    }

    .form-input:focus,
    .form-select:focus,
    .form-textarea:focus {
        border-color: var(--ui-border-strong);
        box-shadow: none;
    }

    .form-select {
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        padding-right: 2.25rem;
        background-color: #fff;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none'%3E%3Cpath d='M6 8l4 4 4-4' stroke='%23a3a3a3' stroke-width='1.75' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 0.65rem center;
        background-size: 1rem 1rem;
    }

    .payment-type-fields-frame {
        border: 1px solid var(--ui-border-default);
        border-radius: 4px;
        padding: 1.25rem;
        background: #fff;
    }

    .finance-billing-summary-copy {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        margin-left: 1rem;
        cursor: pointer;
    }

    .finance-billing-summary-copy:focus {
        outline: none;
    }

    .finance-position-link {
        margin-top: 0.125rem;
        color: var(--ui-text-subtle);
    }

    .finance-position-link:hover,
    .finance-position-link:focus {
        color: var(--ui-text-muted);
    }

    textarea.form-input {
        min-height: auto;
    }

    .finance-date-input {
        padding-right: 2.75rem;
    }

    .finance-date-input--empty {
        color: var(--ui-text-subtle);
    }

    .finance-date-input--empty::-webkit-datetime-edit {
        color: var(--ui-text-subtle);
    }

    .finance-date-input::-webkit-calendar-picker-indicator {
        opacity: 0;
        cursor: pointer;
    }

    .finance-date-picker-btn {
        position: absolute;
        top: 0;
        right: 0;
        width: 2.5rem;
        height: 100%;
        border: 0;
        background: transparent;
        color: var(--ui-text-muted);
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }

    .finance-date-picker-btn:hover {
        color: var(--ui-text-default);
    }

    .finance-date-picker-btn:disabled {
        color: var(--ui-border-strong);
        cursor: not-allowed;
    }
}
</style>
